import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import { RtError } from '@core/utils-legacy';

import { WindowRef } from './common/window-ref';
import type { NotificationRef } from './notifications/notification-ref';
import { NotificationsController } from './notifications/notifications.controller';

export class ClipboardCopyUnavailableError extends RtError {
  constructor() {
    super(
      'ClipboardCopyUnavailableError',
      'ClipboardCopy is not supported (createRange or execCommand doesnt exists)',
    );
  }
}

@Injectable({ providedIn: 'root' })
export class ClipboardCopy {
  // If it is not available and you will try to invoke fromString/fromHtmlElement,
  // it will throw, you should verify it by yourself or wrap invocations of this class methods
  // in try-catch block

  private isAvailable: boolean =
    this.document.createRange != null && this.document.execCommand != null;
  private defaultMessage: string;
  private copiedTextsBuffer: string[] = [];
  private notificationRef: NotificationRef | null = null;

  constructor(
    private i18n: I18nService,
    private notificationsCtrl: NotificationsController,
    private window: WindowRef,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.defaultMessage = this.i18n.translate('rtu.common.copied');

    if (!this.isAvailable) {
      this.fromString = () => {
        throw new ClipboardCopyUnavailableError();
      };
      this.fromHtmlElement = () => {
        throw new ClipboardCopyUnavailableError();
      };
    }
  }

  public fromString(string: string, notificationTitle?: string | null): true {
    const sourceElement: HTMLDivElement = this.createSourceElement();
    // update source element
    sourceElement.innerText = string;
    // select node contents
    this.document.createRange().selectNodeContents(sourceElement);

    // copy node contents to clipboard
    this.fromHtmlElement(sourceElement, notificationTitle);
    sourceElement.remove();
    return true;
  }

  public fromHtmlElement(
    element: HTMLElement,
    notificationTitle: string | null = this.defaultMessage,
  ): true {
    let copiedText: string;

    if (this.isInputOrTextarea(element)) {
      element.select();
      copiedText = this.window.nativeWindow.getSelection()!.toString();
      this.document.execCommand('copy');
    } else {
      const range = this.document.createRange();
      const selection = this.window.nativeWindow.getSelection()!;

      range.selectNodeContents(element);
      selection.removeAllRanges();
      selection.addRange(range);

      copiedText = selection.toString();
      this.document.execCommand('copy');
      selection.removeAllRanges();
    }

    if (!this.copiedTextsBuffer.includes(copiedText) && notificationTitle) {
      if (this.notificationRef) {
        this.notificationRef.close();
      }

      this.notificationRef = this.notificationsCtrl.success({ title: notificationTitle });
      this.notificationRef.afterClosed.subscribe(() => {
        this.copiedTextsBuffer = this.copiedTextsBuffer.filter(text => text !== copiedText);
      });

      this.copiedTextsBuffer = [...this.copiedTextsBuffer, copiedText];
    }

    return true;
  }

  private createSourceElement(): HTMLDivElement {
    const el = this.document.createElement('div');
    el.id = 'clipboard-copy-source';
    el.style.position = 'fixed';
    el.style.left = '0';
    el.style.top = '0';
    el.style.height = '0';
    el.style.width = '0';
    el.style.fontSize = '10px';
    el.style.pointerEvents = 'none';
    this.document.body.appendChild(el);
    return el;
  }

  private isInputOrTextarea(
    element: HTMLElement,
  ): element is HTMLInputElement | HTMLTextAreaElement {
    const tagNameLowerCase = element.tagName.toLowerCase();
    return 'input' === tagNameLowerCase || 'textarea' === tagNameLowerCase;
  }
}
